import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../../lib/auth";
import { getAutomation, type AutomationView } from "../../../../lib/marketingAutomation";
import { getRetention, type RetentionView } from "../../../../lib/marketingRetention";
import { PageHead } from "../../../ui";
import { Panel, dt, num } from "../shared";
import { CampaignsCard } from "./CampaignsCard";
import { RampCard } from "./RampCard";
import { RetentionCard } from "./RetentionCard";
import { ScheduleCard } from "./ScheduleCard";
import { StopCard } from "./StopCard";

/**
 * Marketing -> Automation: the clock over the sender.
 *
 * THE ORDER OF THE CARDS IS THE ORDER OF THE QUESTIONS. Is anything allowed to
 * go out at all (the hard stop), how much may the domain carry today (the
 * ramp), when does the clock ask (the schedule), which campaigns would it pick
 * up (and why not the others), and what has it actually done. Putting the stop
 * anywhere but first would bury the one control somebody opens this page in a
 * hurry to find.
 *
 * ⚠ NOTHING ON THIS PAGE DECIDES WHETHER A LETTER MAY GO OUT. Every limit shown
 * here is enforced by the sender, which answers a batch an operator starts by
 * hand exactly the same way. A ramp only the scheduler honoured would be
 * operator discipline wearing a different hat, because the manual button is
 * right there.
 *
 * The last card is the exception to all of that: retention is not about sending
 * at all. It is here because it is the other thing that happens on a clock
 * above every campaign, and because this is the page somebody opens when the
 * question is "what is this module doing while nobody is watching".
 */
export function AutomationPage({ user }: { user: StaffUser }) {
  const t = useT();
  const canManage = userCanPlatform(user, "manage:crm.marketing");
  const [view, setView] = useState<AutomationView | null>(null);
  const [retention, setRetention] = useState<RetentionView | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    // Two answers rather than one. Retention is a different clock over a
    // different thing - it deletes whether or not the sender is running - and
    // folding it into the automation payload would tie a page that must keep
    // working to whether the sending half could be measured.
    getAutomation()
      .then(setView)
      .catch(() => setView(null))
      .finally(() => setLoading(false));
    getRetention().then(setRetention).catch(() => setRetention(null));
  }, []);

  useEffect(() => load(), [load]);

  return (
    <>
      <PageHead
        title={t("marketing.auto.title")}
        sub={t("marketing.auto.intro")}
        actions={
          <Link to="/marketing/campaigns" className="crm-btn">
            <ChevronLeft className="size-4" />
            {t("marketing.back")}
          </Link>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      ) : !view ? (
        <Panel title={t("marketing.auto.title")}>
          <p className="crm-sub">{t("marketing.auto.loadFailed")}</p>
        </Panel>
      ) : (
        <div className="space-y-4">
          <StopCard stop={view.stop} canManage={canManage} onChanged={load} />
          <RampCard view={view} />
          <ScheduleCard view={view} canManage={canManage} onChanged={load} />
          <CampaignsCard campaigns={view.campaigns} />

          {/* Last, because it is the only card here that is not about sending.
              It answers what happens to everything the sending left behind. */}
          {retention ? (
            <RetentionCard view={retention} canManage={canManage} onChanged={load} />
          ) : null}

          <Panel title={t("marketing.auto.runsTitle")} description={t("marketing.auto.runsIntro")}>
            {view.recentRuns.length === 0 ? (
              <p className="crm-sub">{t("marketing.run.historyEmpty")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="crm-table w-full">
                  <thead>
                    <tr>
                      <th>{t("marketing.run.colStarted")}</th>
                      <th>{t("marketing.run.colTrigger")}</th>
                      <th className="text-right">{t("marketing.run.colSent")}</th>
                      <th className="text-right">{t("marketing.run.colSkipped")}</th>
                      <th className="text-right">{t("marketing.run.colFailed")}</th>
                      <th className="pl-4">{t("marketing.run.colState")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.recentRuns.map((r) => (
                      <tr key={r.id}>
                        <td>{dt(r.startedAt)}</td>
                        <td>{t(`marketing.run.trigger.${r.trigger}`)}</td>
                        <td className="crm-tnum text-right">{num(r.sent)}</td>
                        <td className="crm-tnum text-right">{num(r.skipped)}</td>
                        <td className="crm-tnum text-right">{num(r.failed)}</td>
                        <td className="crm-sub pl-4">
                          {r.error ?? (r.finishedAt ? t("marketing.run.finished") : t("marketing.run.running"))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      )}
    </>
  );
}
